import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API from "../constants/api-endpoints";

// ─── Dropdowns ────────────────────────────────────────────────────────────────

export const useHRUsers = (cid: number) =>
  useQuery({ queryKey: ["hr-users", cid], queryFn: () => client.get(API.HR_USERS(cid)).then(r => r.data.data) });

// ─── Leave Requests ───────────────────────────────────────────────────────────

export const useLeave = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-leave", cid, params], queryFn: () => client.get(API.HR_LEAVE(cid), { params }).then(r => r.data) });

export const useLeaveCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_LEAVE(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-leave", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useLeaveUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.HR_LEAVE(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-leave", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useLeaveDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_LEAVE(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-leave", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Holiday Entitlements ─────────────────────────────────────────────────────

export const useHoliday = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-holiday", cid, params], queryFn: () => client.get(API.HR_HOLIDAY(cid), { params }).then(r => r.data.data) });

export const useHolidayUpsert = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_HOLIDAY(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-holiday", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useHolidayDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_HOLIDAY(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-holiday", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Sickness Absences ────────────────────────────────────────────────────────

export const useSickness = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-sickness", cid, params], queryFn: () => client.get(API.HR_SICKNESS(cid), { params }).then(r => r.data) });

export const useSicknessCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_SICKNESS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-sickness", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useSicknessUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.HR_SICKNESS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-sickness", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useSicknessDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_SICKNESS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-sickness", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Statutory Payments ───────────────────────────────────────────────────────

export const useStatutory = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-statutory", cid, params], queryFn: () => client.get(API.HR_STATUTORY(cid), { params }).then(r => r.data.data) });

export const useStatutoryCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_STATUTORY(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-statutory", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useStatutoryUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.HR_STATUTORY(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-statutory", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useStatutoryDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_STATUTORY(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-statutory", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Timesheets ───────────────────────────────────────────────────────────────

export const useTimesheets = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-timesheets", cid, params], queryFn: () => client.get(API.HR_TIMESHEETS(cid), { params }).then(r => r.data) });

export const useTimesheetCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_TIMESHEETS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-timesheets", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useTimesheetUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.HR_TIMESHEETS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-timesheets", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useTimesheetDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_TIMESHEETS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-timesheets", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useTimesheetEntries = (cid: number, tsId: number) =>
  useQuery({ queryKey: ["hr-ts-entries", cid, tsId], queryFn: () => client.get(API.HR_TIMESHEET_ENTRIES(cid, tsId)).then(r => r.data.data), enabled: !!tsId });

export const useTimesheetEntryCreate = (cid: number, tsId: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_TIMESHEET_ENTRIES(cid, tsId), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-ts-entries", cid, tsId] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useTimesheetEntryDelete = (cid: number, tsId: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_TIMESHEET_ENTRY_DEL(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-ts-entries", cid, tsId] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── NMW ─────────────────────────────────────────────────────────────────────

export const useNMWRates = () =>
  useQuery({ queryKey: ["nmw-rates"], queryFn: () => client.get(API.HR_NMW_RATES()).then(r => r.data.data) });

export const useNMWRateCreate = (cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_NMW_RATES(), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["nmw-rates"] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useNMWChecks = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-nmw-checks", cid, params], queryFn: () => client.get(API.HR_NMW_CHECKS(cid), { params }).then(r => r.data.data) });

export const useNMWCheckCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_NMW_CHECKS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-nmw-checks", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useNMWCheckDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_NMW_CHECKS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-nmw-checks", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Pension ─────────────────────────────────────────────────────────────────

export const usePensionSettings = (cid: number) =>
  useQuery({ queryKey: ["hr-pension-settings", cid], queryFn: () => client.get(API.HR_PENSION_SETTINGS(cid)).then(r => r.data.data) });

export const usePensionSettingCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_PENSION_SETTINGS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-pension-settings", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const usePensionSettingUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.HR_PENSION_SETTINGS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-pension-settings", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const usePensionSettingDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.HR_PENSION_SETTINGS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-pension-settings", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const usePensionEnrolments = (cid: number, params?: any) =>
  useQuery({ queryKey: ["hr-pension-enrolments", cid, params], queryFn: () => client.get(API.HR_PENSION_ENROLMENTS(cid), { params }).then(r => r.data.data) });

export const usePensionEnrolmentCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.HR_PENSION_ENROLMENTS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-pension-enrolments", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const usePensionEnrolmentUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.HR_PENSION_ENROLMENTS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["hr-pension-enrolments", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};
